import { EntityListWithPagination } from "src/lib/itaptec/http_client/src";
import {
  GridEventListener,
  GridRowIdGetter,
  GridRowSelectionModel,
  GridValidRowModel,
  useGridApiRef,
} from "@mui/x-data-grid";
import { useEffect, useState } from "react";

/**
 * Return type of the `useCheckboxSelection` hook.
 */
export type UseCheckboxSelection<TEntity extends GridValidRowModel> = {
  selectedRows: TEntity[];
};

// Overloads para el hook, con y sin paginación.

export function useCheckboxSelection<TEntity extends GridValidRowModel>(
  data: TEntity[] | undefined,
  getRowId: GridRowIdGetter<TEntity>,
  apiRef: ReturnType<typeof useGridApiRef>
): UseCheckboxSelection<TEntity>;

export function useCheckboxSelection<TEntity extends GridValidRowModel>(
  data: EntityListWithPagination<TEntity> | undefined,
  getRowId: GridRowIdGetter<TEntity>,
  apiRef: ReturnType<typeof useGridApiRef>
): UseCheckboxSelection<TEntity>;

/**
 *
 * @param data The data currently displayed in the Data Grid.
 * @param getRowId Function to get the id of a given row.
 */
export function useCheckboxSelection<TEntity extends GridValidRowModel>(
  data: TEntity[] | EntityListWithPagination<TEntity> | undefined,
  getRowId: GridRowIdGetter<TEntity>,
  apiRef: ReturnType<typeof useGridApiRef>
) {
  if (Array.isArray(data)) {
    return useCheckboxSelectionBasic(data, getRowId, apiRef);
  }

  return useCheckboxSelectionPaginated(data, getRowId, apiRef);
}

// Implementaciones del overload con y sin paginación.

function useCheckboxSelectionPaginated<TEntity extends GridValidRowModel>(
  data: EntityListWithPagination<TEntity> | undefined,
  getRowId: GridRowIdGetter<TEntity>,
  apiRef: ReturnType<typeof useGridApiRef>
) {
  const [selectedRows, setSelectedRows] = useState<TEntity[]>([]);

  useEffect(() => {
    const handleRowClick: GridEventListener<"rowSelectionCheckboxChange"> = ({
      id,
      value,
    }) => {
      if (!data) return;

      if (value) {
        const newValue = data.lista.find((x) => getRowId(x) === id);
        newValue && setSelectedRows((prev) => [...prev, newValue]);
      } else {
        setSelectedRows((prev) => [...prev.filter((x) => getRowId(x) !== id)]);
      }
    };

    // The `subscribeEvent` method will automatically unsubscribe in the cleanup function of the `useEffect`.
    return apiRef.current.subscribeEvent(
      "rowSelectionCheckboxChange",
      handleRowClick
    );
  }, [apiRef, data]);

  return {
    selectedRows,
  };
}

function useCheckboxSelectionBasic<TEntity extends GridValidRowModel>(
  data: TEntity[] | undefined,
  getRowId: GridRowIdGetter<TEntity>,
  apiRef: ReturnType<typeof useGridApiRef>
) {
  const [selectedRows, setSelectedRows] = useState<TEntity[]>([]);

  useEffect(() => {
    const handleRowClick: GridEventListener<"rowSelectionCheckboxChange"> = ({
      id,
      value,
    }) => {
      if (!data) return;

      if (value) {
        const newValue = data.find((x) => getRowId(x) === id);
        newValue && setSelectedRows((prev) => [...prev, newValue]);
      } else {
        setSelectedRows((prev) => [...prev.filter((x) => getRowId(x) !== id)]);
      }
    };

    // The `subscribeEvent` method will automatically unsubscribe in the cleanup function of the `useEffect`.
    return apiRef.current.subscribeEvent(
      "rowSelectionCheckboxChange",
      handleRowClick
    );
  }, [apiRef, data]);

  return {
    selectedRows,
  };
}
